import { createFileRoute, redirect } from '@tanstack/react-router';
import { useTranslator } from '@/hooks/useTranslator';
import { getCompanyId } from '@/lib/utils/apiFetch';
import { checkAuthGuard } from '@/lib/utils/auth';
import {
  isImpersonating,
  isScopedToCustomerCompany,
  postLoginRoute,
} from '@/lib/utils/managementAccess';
import {
  bootstrapSession,
  writeImpersonationContext,
} from '@/lib/utils/session';
import { LoginBrandPanel } from '../-components/LoginBrandPanel';
import { LoginForm, type LoginFormLabels } from '../-components/LoginForm';
import {
  PasskeyOffer,
  type PasskeyOfferLabels,
} from '../-components/PasskeyOffer';
import { useLoginFlow } from '../-hooks/useLoginFlow';

/**
 * `/` is the login page. It also absorbs what the old `app/index.tsx` did —
 * sending an authenticated visitor on to the dashboard — because two files
 * cannot answer the same path.
 *
 * The root route treats `/` as public and skips session bootstrap there, so
 * this guard is the only thing between a signed-in user and a login form they
 * have no use for.
 */
export const Route = createFileRoute('/(login)/(pages)/')({
  beforeLoad: async () => {
    const { allowed } = await checkAuthGuard();
    if (!allowed) return;

    // Retraced staff arrive for the management tools, not the customer app, so
    // they are sent there instead of to `/dashboard`. `/management` forwards on
    // to whichever tool is the default, and its own guard re-checks access —
    // this only decides where to point someone.
    //
    // `checkAuthGuard` has already seeded the session from the hand-off in the
    // URL fragment, so the fetch below sees the token the old dashboard just
    // passed over. A null `me` (the fetch failed) falls through to the
    // dashboard, which is where every non-staff user goes anyway.
    let me = await bootstrapSession();

    // An operator who switched company in the old dashboard and then came over
    // arrives as staff scoped to a customer company, with no impersonation:
    // the old dashboard has no customer-area guard, so a bare company override
    // is enough to browse there. Here it is not — `postLoginRoute` would send
    // them to `/management`, which is why "switch company, then Try now"
    // landed on the tools instead of the company they were looking at.
    //
    // Impersonating themselves at that company is the same state the v2
    // switcher produces, and it is what the operator already meant. `/users/me`
    // is then re-read so the session carries the server's side of it too —
    // `unImpersonatedUser`, which the header and the way out both read.
    if (
      me &&
      !isImpersonating(me) &&
      isScopedToCustomerCompany(me) &&
      writeImpersonationContext(me.id, getCompanyId())
    ) {
      me = await bootstrapSession();
    }

    throw redirect({ to: postLoginRoute(me) });
  },
  component: LoginPage,
});

function LoginPage() {
  const { getTranslation } = useTranslator({ page: 'login' });
  const flow = useLoginFlow();

  const labels: LoginFormLabels = {
    tagline: getTranslation('form.tagline'),
    emailLabel: getTranslation('form.emailLabel'),
    emailPlaceholder: getTranslation('form.emailPlaceholder'),
    codeLabel: getTranslation('form.codeLabel'),
    codeSent: getTranslation('form.codeSent'),
    back: getTranslation('form.back'),
    resend: getTranslation('form.resend'),
    resendIn: getTranslation('form.resendIn'),
    requestCode: getTranslation('form.requestCode'),
    submitCode: getTranslation('form.submitCode'),
    or: getTranslation('form.or'),
    sso: getTranslation('form.sso'),
    ssoEnforced: getTranslation('form.ssoEnforced'),
    checkingSso: getTranslation('form.checkingSso'),
    systemDownTitle: getTranslation('errors.systemDownTitle'),
    systemDownBody: getTranslation('errors.systemDownBody'),
    statusPage: getTranslation('errors.statusPage'),
    legalPrefix: getTranslation('legal.prefix'),
    privacyPolicy: getTranslation('legal.privacyPolicy'),
    legalJoin: getTranslation('legal.join'),
    imprint: getTranslation('legal.imprint'),
  };

  const passkeyOfferLabels: PasskeyOfferLabels = {
    title: getTranslation('passkeyOffer.title'),
    body: getTranslation('passkeyOffer.body'),
    create: getTranslation('passkeyOffer.create'),
    notNow: getTranslation('passkeyOffer.notNow'),
    createdTitle: getTranslation('passkeyOffer.createdTitle'),
    createdBody: getTranslation('passkeyOffer.createdBody'),
    continue: getTranslation('passkeyOffer.continue'),
  };

  return (
    // Two equal halves, each with a floor so the form column can never be
    // squeezed by the panel beside it. The panel hides itself below `lg`.
    // `data-login-surface` steps type and control sizes up for this screen —
    // see the scope block in globals.css.
    <div
      data-login-surface
      className='grid min-h-dvh grid-cols-1 lg:grid-cols-[minmax(26rem,1fr)_minmax(26rem,1fr)]'
    >
      <LoginForm
        step={flow.step}
        email={flow.email}
        code={flow.code}
        emailError={flow.emailError}
        codeError={flow.codeError}
        isSystemUnreachable={flow.isSystemUnreachable}
        isSubmitting={flow.isSubmitting}
        isRequestingSso={flow.isRequestingSso}
        resendCountdown={flow.resendCountdown}
        isSsoOnly={flow.isSsoOnly}
        isCheckingSso={flow.isCheckingSso}
        labels={labels}
        passkeyOffer={
          <PasskeyOffer
            state={flow.passkeyOffer.state}
            error={flow.passkeyOffer.error}
            isCreating={flow.passkeyOffer.isCreating}
            isDismissing={flow.passkeyOffer.isDismissing}
            labels={passkeyOfferLabels}
            onCreate={flow.passkeyOffer.onCreate}
            onDismiss={flow.passkeyOffer.onDismiss}
            onContinue={flow.passkeyOffer.onContinue}
          />
        }
        onEmailChange={flow.onEmailChange}
        onCodeChange={flow.onCodeChange}
        onSubmit={flow.onSubmit}
        onBack={flow.onBack}
        onResend={flow.onResend}
        onSso={flow.onSso}
      />
      <LoginBrandPanel
        headline={getTranslation('panel.headline')}
        description={getTranslation('panel.description')}
      />
    </div>
  );
}
